<template>
  <section class="billing-section">
    <div>
      <h3 class="text-xl font-bold text-neutral-900">{{ $t('Billing') }}</h3>
      <p class="text-neutral-600 mt-1 mb-6">{{ $t('Manage your subscription, payment methods, and billing details.') }}</p>

      <!-- Billing Warning Message -->
      <transition-group name="fade-slide" tag="div">
        <div
          v-for="warning in getWarningMessages"
          :key="warning"
          class="billing-alert mb-4"
        >
          <div class="billing-alert__icon">
            <svg viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2L1 21h22L12 2zm0 3.45l8 14.1H4l8-14.1zM11 16h2v2h-2v-2zm0-7h2v5h-2V9z"/>
            </svg>
          </div>
          <div class="billing-alert__content">
            <span class="billing-alert__title">{{ $t('Important') }}</span>
            <p class="billing-alert__message">{{ warning }}</p>
          </div>
        </div>
      </transition-group>

      <div v-if="error" class="billing-alert mb-4">
        <div class="billing-alert__icon">
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 2L1 21h22L12 2zm0 3.45l8 14.1H4l8-14.1zM11 16h2v2h-2v-2zm0-7h2v5h-2V9z"/>
          </svg>
        </div>
        <div class="billing-alert__content">
          <span class="billing-alert__title">{{ $t('Important') }}</span>
          <p class="billing-alert__message">{{ error }}</p>
        </div>
      </div>

      <!-- Unlimited/Lifetime Plan -->
      <unlimited-plan
        v-if="
          isCodeActivated &&
          (currentPlan.name == 'unlimited' || currentPlan.name == 'lifetime')
        "
        :unlimited-plan="unlimited"
      />

      <div v-else>
        <!-- Current Plan & Quick Actions Row -->
        <div class="billing-top-row" :class="{ 'billing-top-row--single': !showMenu }">
          <!-- Quick Actions Menu -->
          <div v-if="showMenu" class="billing-quick-actions">
            <a
              class="billing-action-link"
              href="#"
              @click.prevent="openPortal('portal_payment_method', 'payment_method_update')"
            >
              <span class="billing-action-link__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <rect x="2" y="5" width="20" height="14" rx="2"/>
                  <path d="M2 10h20"/>
                </svg>
              </span>
              <span class="billing-action-link__text">{{ $t('Update payment method') }}</span>
              <loader v-if="loaderActive.portal_payment_method" border-color="#374151" />
              <svg v-else class="billing-action-link__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </a>
            <a
              class="billing-action-link"
              href="#"
              @click.prevent="openPortal('portal_billing_details')"
            >
              <span class="billing-action-link__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/>
                  <circle cx="9" cy="7" r="4"/>
                  <path d="M22 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75"/>
                </svg>
              </span>
              <span class="billing-action-link__text">{{ $t('Update billing details') }}</span>
              <loader v-if="loaderActive.portal_billing_details" border-color="#374151" />
              <svg v-else class="billing-action-link__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </a>
            <a
              class="billing-action-link"
              href="#"
              @click.prevent="openPortal('portal_history')"
            >
              <span class="billing-action-link__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                  <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
                </svg>
              </span>
              <span class="billing-action-link__text">{{ $t('View payment history') }}</span>
              <loader v-if="loaderActive.portal_history" border-color="#374151" />
              <svg v-else class="billing-action-link__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </a>
            <a
              class="billing-action-link billing-action-link--danger"
              href="#"
              @click.prevent="openPortal('portal_cancel', 'subscription_cancel')"
            >
              <span class="billing-action-link__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M15 9l-6 6M9 9l6 6"/>
                </svg>
              </span>
              <span class="billing-action-link__text">{{ $t('Cancel subscription') }}</span>
              <loader v-if="loaderActive.portal_cancel" border-color="#dc2626" />
              <svg v-else class="billing-action-link__arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M9 18l6-6-6-6"/>
              </svg>
            </a>
          </div>

          <!-- Current Plan Card -->
          <div class="billing-current-plan">
            <div class="billing-current-plan__header">
              <span class="billing-current-plan__label">{{ $t('Current Plan') }}</span>
              <span
                v-if="currentPlan.active_status !== false"
                class="billing-current-plan__badge"
              >{{ $t('Active') }}</span>
              <span
                v-else
                class="billing-current-plan__badge billing-current-plan__badge--expired"
              >{{ $t('Expired') }}</span>
            </div>
            <div class="billing-current-plan__name">{{ getCurrentPlanName }}</div>

            <div v-if="currentPlan.paid_out_of_stripe" class="billing-current-plan__contact">
              {{ $t('For account changes, contact') }}
              <a href="mailto:sales@feedbear.com">sales@feedbear.com</a>
            </div>

            <div v-if="currentPlan.name == 'trial'" class="billing-current-plan__trial">
              <template v-if="currentPlan.active_status">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M12 6v6l4 2"/>
                </svg>
                <span>{{ $t('Trial ends') }}<template v-if="nextPaymentDate"> {{ nextPaymentDate }}</template></span>
              </template>
              <template v-else>
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
                </svg>
                <span>{{ $t('Trial expired') }}<template v-if="nextPaymentDate"> {{ nextPaymentDate }}</template>. {{ $t('Upgrade to re-activate.') }}</span>
              </template>
            </div>

            <div v-if="showMenu" class="billing-current-plan__price">
              <span class="billing-current-plan__price-amount">
                ${{ currentSubscriptionPrice }}
              </span>
              <span class="billing-current-plan__price-period">
                /{{ currentPlan.is_annual_billing_period ? $t('year') : $t('month') }}
              </span>
            </div>

            <div v-if="currentPlan.stripe_payment_failed" class="billing-payment-failed">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"/>
              </svg>
              <span>{{ $t('Payment failed. Please update your billing details.') }}</span>
            </div>
          </div>
        </div>

        <!-- Plans Section -->
        <div v-if="!currentPlan.paid_out_of_stripe" class="billing-plans-section">
          <div class="billing-plans-header">
            <h4 class="billing-plans-title">{{ $t('Available Plans') }}</h4>
            <div class="billing-toggle">
              <span
                class="billing-toggle__label"
                :class="{ 'billing-toggle__label--active': !isAnnualPlan }"
              >{{ $t('Monthly') }}</span>
              <button
                type="button"
                class="billing-toggle__switch"
                :class="{ 'billing-toggle__switch--active': isAnnualPlan }"
                @click="isAnnualPlan = !isAnnualPlan"
                role="switch"
                :aria-checked="isAnnualPlan"
              >
                <span class="billing-toggle__knob"></span>
              </button>
              <span
                class="billing-toggle__label"
                :class="{ 'billing-toggle__label--active': isAnnualPlan }"
              >
                {{ $t('Annual') }}
                <span class="billing-toggle__save">{{ $t('Save 17%') }}</span>
              </span>
            </div>
          </div>

          <!-- Plan Cards Grid -->
          <div class="billing-plans-grid">
            <!-- Lite Plan -->
            <div
              class="billing-plan-card"
              :class="{ 'billing-plan-card--disabled': !isLiteApplicable, 'billing-plan-card--current': currentPlan.name == litePlanData.name }"
            >
              <div class="billing-plan-card__content">
                <div class="billing-plan-card__header">
                  <h3 class="billing-plan-card__name">{{ $t('Lite') }}</h3>
                  <span v-if="currentPlan.name == litePlanData.name" class="billing-plan-card__current-badge">{{ $t('Current') }}</span>
                </div>
                <div class="billing-plan-card__pricing">
                  <span class="billing-plan-card__price">${{ isAnnualPlan ? litePlanData.price.annually : litePlanData.price.monthly }}</span>
                  <span class="billing-plan-card__period">/{{ $t('month') }}</span>
                </div>
                <div class="billing-plan-card__action">
                  <button
                    v-if="currentPlan.name == litePlanData.name"
                    class="billing-plan-btn billing-plan-btn--current"
                    disabled
                  >{{ $t('Current Plan') }}</button>
                  <button
                    v-else-if="currentPlan.order < litePlanData.order"
                    class="billing-plan-btn billing-plan-btn--upgrade"
                    :disabled="!isLiteApplicable"
                    @click="handlePlanUpgradeSelection('lite')"
                  >
                    <span>{{ $t('Upgrade') }}</span>
                    <loader v-if="loaderActive.lite_yearly || loaderActive.lite_monthly" border-color="#fff" />
                  </button>
                  <button
                    v-else
                    class="billing-plan-btn billing-plan-btn--downgrade"
                    @click.prevent="handlePlanDowngradeSelection('lite')"
                  >
                    <span>{{ $t('Downgrade') }}</span>
                    <loader v-if="loaderActive.lite_yearly || loaderActive.lite_monthly" border-color="#374151" />
                  </button>
                </div>
                <ul class="billing-plan-card__features">
                  <li v-for="feature in litePlanData.features" :key="feature">
                    <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                    <span>{{ feature }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Startup Plan (legacy) -->
            <div
              v-if="currentPlan && currentPlan.name == 'startup'"
              class="billing-plan-card"
              :class="{ 'billing-plan-card--disabled': !isStartupApplicable, 'billing-plan-card--current': currentPlan.name == startupPlanData.name }"
            >
              <div class="billing-plan-card__content">
                <div class="billing-plan-card__header">
                  <h3 class="billing-plan-card__name">{{ $t('Startup') }}</h3>
                  <span v-if="currentPlan.name == startupPlanData.name" class="billing-plan-card__current-badge">{{ $t('Current') }}</span>
                </div>
                <div class="billing-plan-card__pricing">
                  <span class="billing-plan-card__price">${{ isAnnualPlan ? startupPlanData.price.annually : startupPlanData.price.monthly }}</span>
                  <span class="billing-plan-card__period">/{{ $t('month') }}</span>
                </div>
                <div class="billing-plan-card__action">
                  <button
                    v-if="currentPlan.name == startupPlanData.name"
                    class="billing-plan-btn billing-plan-btn--current"
                    disabled
                  >{{ $t('Current Plan') }}</button>
                  <button
                    v-else-if="currentPlan.order < startupPlanData.order"
                    class="billing-plan-btn billing-plan-btn--upgrade"
                    :disabled="!isStartupApplicable"
                    @click="handlePlanUpgradeSelection('startup')"
                  >
                    <span>{{ $t('Upgrade') }}</span>
                    <loader v-if="loaderActive.startup_yearly || loaderActive.startup_monthly" border-color="#fff" />
                  </button>
                  <button
                    v-else
                    class="billing-plan-btn billing-plan-btn--downgrade"
                    @click.prevent="handlePlanDowngradeSelection('startup')"
                  >
                    <span>{{ $t('Downgrade') }}</span>
                    <loader v-if="loaderActive.startup_yearly || loaderActive.startup_monthly" border-color="#374151" />
                  </button>
                </div>
                <ul class="billing-plan-card__features">
                  <li v-for="feature in startupPlanData.features" :key="feature">
                    <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                    <span>{{ feature }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Startup 2023 Plan -->
            <div
              v-if="!currentPlan || currentPlan.name != 'startup'"
              class="billing-plan-card"
              :class="{ 'billing-plan-card--disabled': !isStartupApplicable, 'billing-plan-card--current': currentPlan.name == startup202304PlanData.name }"
            >
              <div class="billing-plan-card__content">
                <div class="billing-plan-card__header">
                  <h3 class="billing-plan-card__name">{{ $t('Startup') }}</h3>
                  <span v-if="currentPlan.name == startup202304PlanData.name" class="billing-plan-card__current-badge">{{ $t('Current') }}</span>
                </div>
                <div class="billing-plan-card__pricing">
                  <span class="billing-plan-card__price">${{ isAnnualPlan ? startup202304PlanData.price.annually : startup202304PlanData.price.monthly }}</span>
                  <span class="billing-plan-card__period">/{{ $t('month') }}</span>
                </div>
                <div class="billing-plan-card__action">
                  <button
                    v-if="currentPlan.name == startup202304PlanData.name"
                    class="billing-plan-btn billing-plan-btn--current"
                    disabled
                  >{{ $t('Current Plan') }}</button>
                  <button
                    v-else-if="currentPlan.order < startup202304PlanData.order"
                    class="billing-plan-btn billing-plan-btn--upgrade"
                    :disabled="!isStartupApplicable"
                    @click="handlePlanUpgradeSelection('startup_202304')"
                  >
                    <span>{{ $t('Upgrade') }}</span>
                    <loader v-if="loaderActive.startup_yearly || loaderActive.startup_monthly" border-color="#fff" />
                  </button>
                  <button
                    v-else
                    class="billing-plan-btn billing-plan-btn--downgrade"
                    @click.prevent="handlePlanDowngradeSelection('startup_202304')"
                  >
                    <span>{{ $t('Downgrade') }}</span>
                    <loader v-if="loaderActive.startup_yearly || loaderActive.startup_monthly" border-color="#374151" />
                  </button>
                </div>
                <ul class="billing-plan-card__features">
                  <li v-for="feature in startup202304PlanData.features" :key="feature">
                    <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                    <span>{{ feature }}</span>
                  </li>
                </ul>
              </div>
            </div>

            <!-- Business Plan -->
            <div
              class="billing-plan-card"
              :class="{ 'billing-plan-card--disabled': !isBusinessApplicable, 'billing-plan-card--current': currentPlan.name == businessPlanData.name }"
            >
              <div class="billing-plan-card__content">
                <div class="billing-plan-card__header">
                  <h3 class="billing-plan-card__name">{{ $t('Business') }}</h3>
                  <span v-if="currentPlan.name == businessPlanData.name" class="billing-plan-card__current-badge">{{ $t('Current') }}</span>
                </div>
                <div class="billing-plan-card__pricing">
                  <span class="billing-plan-card__price">${{ isAnnualPlan ? businessPlanData.price.annually : businessPlanData.price.monthly }}</span>
                  <span class="billing-plan-card__period">/{{ $t('month') }}</span>
                </div>
                <div class="billing-plan-card__action">
                  <button
                    v-if="currentPlan.name == businessPlanData.name"
                    class="billing-plan-btn billing-plan-btn--current"
                    disabled
                  >{{ $t('Current Plan') }}</button>
                  <button
                    v-else-if="currentPlan.order < businessPlanData.order"
                    class="billing-plan-btn billing-plan-btn--upgrade"
                    :disabled="!isBusinessApplicable"
                    @click="handlePlanUpgradeSelection('business')"
                  >
                    <span>{{ $t('Upgrade') }}</span>
                    <loader v-if="loaderActive.business_monthly || loaderActive.business_yearly" border-color="#fff" />
                  </button>
                  <button
                    v-else
                    class="billing-plan-btn billing-plan-btn--downgrade"
                    @click.prevent="handlePlanDowngradeSelection('business')"
                  >
                    <span>{{ $t('Downgrade') }}</span>
                    <loader v-if="loaderActive.business_monthly || loaderActive.business_yearly" border-color="#374151" />
                  </button>
                </div>
                <ul class="billing-plan-card__features">
                  <li v-for="feature in businessPlanData.features" :key="feature">
                    <svg viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd"/></svg>
                    <span>{{ feature }}</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <!-- Enterprise Card -->
          <div class="billing-enterprise">
            <div class="billing-enterprise__content">
              <div class="billing-enterprise__icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
                </svg>
              </div>
              <div class="billing-enterprise__text">
                <h4>{{ $t('Need more team members or custom features?') }}</h4>
                <p>{{ $t('Contact us for our Enterprise plan starting from $299/month') }}</p>
              </div>
            </div>
            <a
              href="https://www.feedbear.com/contact-us"
              target="_blank"
              class="billing-enterprise__btn"
            >
              {{ $t('Contact Sales') }}
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </a>
          </div>
        </div>
      </div>
    </div>
    <Dialog
      :title="changePlanDialog ? $t('Important!') : $t('Changes needed!')"
      v-model="dialogVisible"
      :isBilling="true"
    >
      <div class="message-dialog px-4">
        <div v-if="!changePlanDialog">
          <h2 class="dialog-heading mb-4 px-5 text-base">
            {{ $t('Before downgrading your plan please change the following:') }}
          </h2>

          <ul class="list-disc list-outside px-12">
            <li
              class="mb-2"
              v-for="message in getComparisonMessages"
              :key="message"
            >
              <span class="text-base" style="color: #575757">{{
                message
              }}</span>
            </li>
          </ul>

          <div class="flex justify-center items-center mt-4 mb-7 space-x-2">
            <button
              class="btn-main"
              @click="dialogVisible = false"
            >
              {{ $t('Ok, I understand') }}
            </button>
          </div>
        </div>
        <div v-else>
          <div class="text-base font-normal text-neutral-800">
            <p class="mb-3">
              {{ $t('If you downgrade your plan features and limits that are only available to higher tiers will be automatically updated.') }}
            </p>
            <p>
              {{ $t('We recommend checking these details before downgrading your plan and making sure they match the features and limits available on your new chosen plan.') }}
            </p>
          </div>
          <div class="flex justify-end items-center mt-4 mb-7 gap-2">
            <button class="btn-secondary" @click="dialogVisible = false">
              {{ $t('Cancel') }}
            </button>
            <button
              class="btn-main"
              @click="
                dialogVisible = false;
                changePlanDialog = false;
                loaderActive['change_plan'] = true;
                updatePlan();
              "
            >
              {{ $t('Ok, I understand') }}
            </button>
          </div>
        </div>
      </div>
    </Dialog>
  </section>
</template>
<script>
import Dialog from "../components/dialog";
import UnlimitedPlan from "../components/unlimited_plan";
import loader from "./spinner";
import { mapGetters } from "vuex";

export default {
  data() {
    return {
      dialogVisible: false,
      changePlanDialog: false,
      isAnnualPlan: false,
      showCorporate: false,
      error: null,
      planUpgradeOrDowngradeMessages: [],
      allAvailablePlans: [
        "lite",
        "startup",
        "business",
        "business_202306",
        "professional",
        "stripe_monthly",
        "stripe_yearly",
        "corporate_50",
        "corporate_100",
        "corporate",
        "startup_202304",
      ],
      litePlanData: {
        name: "lite",
        price: {
          monthly: "19",
          annually: "15",
        },
        order: 1,
        features: [],
      },
      startupPlanData: {
        name: "startup",
        price: {
          monthly: "29",
          annually: "24",
        },
        order: 2,
        features: [],
      },
      startup202304PlanData: {
        name: "startup_202304",
        price: {
          monthly: "49",
          annually: "40",
        },
        order: 2,
        features: [],
      },
      businessPlanData: {
        name: "business",
        price: {
          monthly: "99",
          annually: "82",
        },
        order: 3,
        features: [],
      },
      business202306PlanData: {
        name: "business",
        price: {
          monthly: "199",
          annually: "165",
        },
        order: 3,
        features: [],
      },
      professionalPlanData: {
        name: "professional",
        price: {
          monthly: "199",
          annually: "1,990",
        },
        order: 3,
        features: [],
      },
      enterprisePlanData: {
        name: "enterprise",
        price: {
          monthly: "299",
          annually: "249",
        }
      },
      customPlanData: {
        name: "Custom",
        features: [],
      },
      corporatePlan: [
        {
          name: "corporate_50",
          price: {
            monthly: "299",
            annually: "2,990",
          },
          order: 4,
          features: [],
        },
        {
          name: "corporate_100",
          price: {
            monthly: "499",
            annually: "4,990",
          },
          order: 5,
          features: [],
        },
      ],
      loaderActive: {
        change_plan: false,
        portal_payment_method: false,
        portal_billing_details: false,
        portal_history: false,
        portal_cancel: false,
        lite_monthly: false,
        lite_yearly: false,
        startup_monthly: false,
        startup_yearly: false,
        business_monthly: false,
        business_yearly: false,
        professional_monthly: false,
        professional_yearly: false,
        corporate_50_monthly: false,
        corporate_50_yearly: false,
        corporate_100_monthly: false,
        corporate_100_yearly: false,
      },
      unlimited: {},
    };
  },
  props: {
    currentPlan: Object,
    currentLoggedInUser: Object,
    isCodeActivated: Boolean,
  },
  components: {
    Dialog,
    loader,
    UnlimitedPlan,
  },
  created() {
    this.litePlanData.features = [
      this.$t('1 team member'),
      this.$t('1 board'),
      this.$t('Unlimited ideas'),
      this.$t('Unlimited users'),
    ];
    this.startupPlanData.features = [
      this.$t('3 team members'),
      this.$t('Unlimited boards'),
      this.$t('Custom domain'),
      this.$t('Integrations with Intercom, Slack, Trello, Jira and Zapier'),
    ];
    this.startup202304PlanData.features = [
      this.$t('3 team members'),
      this.$t('Unlimited boards'),
      this.$t('Custom domain'),
      this.$t('Integrations with Intercom, Slack, Trello, Jira and Zapier'),
    ];
    this.businessPlanData.features = [
      this.$t('10 team members'),
      this.$t('Single Sign On (SSO)'),
      this.$t('Private projects'),
      this.$t('Remove FeedBear branding'),
    ];
    this.business202306PlanData.features = [
      this.$t('10 team members'),
      this.$t('Single Sign On (SSO)'),
      this.$t('Private projects'),
      this.$t('Remove FeedBear branding'),
    ];
    this.professionalPlanData.features = [
      this.$t('20 team members'),
      this.$t('Jira integration'),
      this.$t('Remove Powered by FeedBear'),
    ];
    this.customPlanData.features = [
      this.$t('Unlimited team members'),
      this.$t('Priority support over Slack, Phone, Email'),
      this.$t('Custom Contracts, NDA and SLA'),
      this.$t('Custom DPAs'),
      this.$t('Custom integrations'),
      this.$t('Pay by bank transfer'),
      this.$t('Security audit'),
    ];
    this.corporatePlan[0].features = [this.$t('50 team members')];
    this.corporatePlan[1].features = [this.$t('100 team members')];
  },
  mounted() {
    this.isAnnualPlan =
      this.getCurrentProjectPlan &&
      this.getCurrentProjectPlan.is_annual_billing_period;
    Rails.ajax({
      url: "/get_appsumo_plan",
      type: "GET",
      beforeSend: function () {
        return true;
      },
      success: (data) => {
        this.unlimited = data.plan;
      },
      error: (data, status) => {
        console.log(data);
      },
    });
  },
  computed: {
    ...mapGetters([
      "getCurrentProjectPlan",
      "getWarningMessages",
      "getPlanFeatures",
      "getSubscriptionId",
      "getStripePriceKeys",
      "getStripeSubscription",
    ]),
    nextPaymentDate() {
      // Returns false when there is no usable date — callers must not render
      // the date fragment then (this used to print "Invalid Date"/"false").
      const format = { year: "numeric", month: "long", day: "numeric" };
      const locale = (this.$i18n && this.$i18n.locale) || "en-us";
      const source = this.currentPlan.valid_until || this.currentPlan.trial_valid_until;
      if (!source) return false;
      const date = new Date(source);
      if (isNaN(date.getTime())) return false;
      if (this.currentPlan.valid_until && date < new Date()) return false;
      return date.toLocaleDateString(locale, format);
    },
    isLiteApplicable() {
      if (this.currentPlan.name != "lite") {
        return (
          this.currentPlan.team_count <=
          this.getPlanFeatures[this.litePlanData.name]?.team &&
          this.currentPlan.boards_count <= 1
        );
      } else return true;
    },
    isStartupApplicable() {
      if (this.currentPlan.name != "startup" && this.currentPlan.name != "startup_202304") {
        return (
          this.currentPlan.team_count <=
            this.getPlanFeatures[this.startup202304PlanData.name]?.team &&
          !this.currentPlan.sso_enabled &&
          !this.currentPlan.privacy_enabled
        );
      } else return true;
    },
    isBusinessApplicable() {
      if (this.currentPlan.name != "business") {
        return (
          this.currentPlan.team_count <=
          this.getPlanFeatures[this.businessPlanData.name]?.team
        );
      } else return true;
    },
    getComparisonMessages() {
      return this.$store.state.planMessages;
    },
    isTrialExpired() {
      if (this.currentPlan.trial_valid_until) {
        return new Date(this.currentPlan.trial_valid_until) < new Date();
      } else return false;
    },
    showMenu() {
      return (
        this.allAvailablePlans.includes(this.currentPlan.name) &&
        !this.currentPlan.paid_out_of_stripe
      );
    },
    currentSubscriptionPrice() {
      if (this.getStripeSubscription) {
        if (this.getStripeSubscription.discount)
          return this.discountedPrice(
            this.getStripeSubscription.discount.coupon,
            this.getStripeSubscription["plan"]["amount_decimal"].slice(0, -2)
          );
        else
          return this.getStripeSubscription["plan"]["amount_decimal"].slice(
            0,
            -2
          );
      } else {
        return this.currentPlan.price;
      }
    },
    getCurrentPlanName() {
      return this.currentPlan.paid_out_of_stripe
        ? this.$t("Custom Corporate Plan Paid By Bank Transfer")
        : this.currentPlan["name"]
            .replace("_", " ")
            .replace("202304", "")
            .replace("202306", "");
    },
  },
  methods: {
    handleTrialUpdate(toPlan) {
      this.$emit("plan-selection", toPlan);
    },
    changePlan() {
      if (
        this.currentPlan.order > 1 &&
        (this.currentPlan.sso_enabled ||
          this.currentPlan.privacy_enabled ||
          this.currentPlan.team_count > 3)
      ) {
        this.changePlanDialog = true;
        this.dialogVisible = true;
      } else {
        this.loaderActive["change_plan"] = true;
        this.updatePlan();
      }
    },
    updatePlan() {
      this.openPortal("change_plan", "subscription_update");
    },
    // Stripe portal session URLs carry the auth in a ?secret= query param, so
    // deep links must go through flow_data at session creation — never by
    // appending paths to the returned URL.
    openPortal(loaderKey, flow = null, price = null) {
      this.loaderActive[loaderKey] = true;
      this.error = null;
      const params = new FormData();
      if (flow && !(flow.startsWith("subscription_") && !this.getSubscriptionId)) {
        params.append("flow", flow);
        if (price) params.append("price", price);
      }
      Rails.ajax({
        url: "/customer_portal_sessions",
        type: "POST",
        data: params,
        beforeSend: function () {
          return true;
        },
        success: (data) => {
          window.location.assign(data.portal_url);
        },
        error: (data, status) => {
          this.loaderActive[loaderKey] = false;
          this.error =
            (data && data.message) ||
            this.$t("Something went wrong. Please try again.");
        },
      });
    },
    async validatePlan(plan_name) {
      if (this.changePlanDialog) this.changePlanDialog = false;
      var plan = plan_name.includes("monthly")
        ? plan_name.replace("_monthly", "")
        : plan_name.replace("_yearly", "");
      await this.$store.dispatch(
        "comparePlan",
        this.allAvailablePlans[this.allAvailablePlans.indexOf(plan)]
      );
      setTimeout(() => {
        if (this.getComparisonMessages.length > 0) this.dialogVisible = true;
        else {
          this.currentPlan.name == "lifetime" ||
          this.currentPlan.name == "unlimited"
            ? this.handleTrialUpdate(plan_name)
            : this.toStripePlan(plan_name);
        }
      }, 300);
    },
    toStripePlan(planName) {
      this.openPortal(
        planName,
        "subscription_update_confirm",
        this.getStripePriceKeys[planName]
      );
    },
    handlePlanUpgradeSelection(plan) {
      this.isAnnualPlan
        ? this.allAvailablePlans.includes(this.currentPlan.name)
          ? this.toStripePlan(plan + "_yearly")
          : this.handleTrialUpdate(plan + "_yearly")
        : this.allAvailablePlans.includes(this.currentPlan.name)
        ? this.toStripePlan(plan + "_monthly")
        : this.handleTrialUpdate(plan + "_monthly");
    },
    handlePlanDowngradeSelection(plan) {
      this.isAnnualPlan
        ? this.validatePlan(plan + "_yearly")
        : this.validatePlan(plan + "_monthly");
    },
    discountedPrice(coupon, price) {
      if (coupon.percent_off) return price * ((100 - coupon.percent_off) / 100);
      else if (coupon.amount_off) return price - coupon.amount_off / 100;
    },
  },
};
</script>

<style lang="scss" scoped>
// ============================================
// BILLING PAGE - Clean, Modern Design
// ============================================

.billing-section {
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(8px); }
  to { opacity: 1; transform: translateY(0); }
}

// --------------------------------------------
// Alerts
// --------------------------------------------

.billing-alert {
  display: flex;
  gap: 0.75rem;
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  border: 1px solid #fcd34d;
  border-radius: 12px;

  &__icon {
    flex-shrink: 0;
    width: 1.25rem;
    height: 1.25rem;
    color: #d97706;
    margin-top: 2px;

    svg {
      width: 100%;
      height: 100%;
    }
  }

  &__content {
    flex: 1;
  }

  &__title {
    font-weight: 600;
    color: #92400e;
    font-size: 0.875rem;
  }

  &__message {
    color: #a16207;
    font-size: 0.875rem;
    margin-top: 0.125rem;
  }
}

// --------------------------------------------
// Top Row (Quick Actions + Current Plan)
// --------------------------------------------

.billing-top-row {
  display: grid;
  grid-template-columns: 1fr 1.5fr;
  gap: 1.5rem;
  margin-bottom: 2rem;

  &--single {
    grid-template-columns: 1fr;
  }

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
  }
}

// Quick Actions Menu
.billing-quick-actions {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  padding: 0.5rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
}

.billing-action-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  color: #404040;
  text-decoration: none;
  font-size: 0.875rem;
  font-weight: 500;
  transition: all 0.15s ease;

  &:hover {
    background: #fff;
    color: #1f1f1f;
    text-decoration: none;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 2rem;
    height: 2rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    color: #737373;
    flex-shrink: 0;

    svg {
      width: 1rem;
      height: 1rem;
    }
  }

  &:hover &__icon {
    border-color: var(--accent-color, #3b82f6);
    color: var(--accent-color, #3b82f6);
  }

  &__text {
    flex: 1;
  }

  &__arrow {
    width: 1rem;
    height: 1rem;
    color: #d1d5db;
    opacity: 0;
    transform: translateX(-4px);
    transition: all 0.15s ease;
  }

  &:hover &__arrow {
    opacity: 1;
    transform: translateX(0);
    color: #a3a3a3;
  }

  &--danger {
    color: #dc2626;

    &:hover {
      background: #fef2f2;
      color: #b91c1c;
    }

    .billing-action-link__icon {
      color: #dc2626;
      border-color: #fecaca;
      background: #fef2f2;
    }
  }
}

// Current Plan Card
.billing-current-plan {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;

  &__header {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    margin-bottom: 0.5rem;
  }

  &__label {
    font-size: 0.75rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #737373;
  }

  &__badge {
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.25rem 0.5rem;
    background: #dcfce7;
    color: #166534;
    border-radius: 9999px;

    &--expired {
      background: #fee2e2;
      color: #991b1b;
    }
  }

  &__name {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f1f1f;
    text-transform: capitalize;
    margin-bottom: 1rem;
  }

  &__contact {
    font-size: 0.875rem;
    color: #737373;

    a {
      color: var(--accent-color, #3b82f6);
      font-weight: 500;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__trial {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #f3f4f6;
    border-radius: 8px;
    font-size: 0.875rem;
    color: #404040;

    svg {
      width: 1.25rem;
      height: 1.25rem;
      color: #737373;
      flex-shrink: 0;
    }
  }

  &__price {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
    margin-top: 1rem;
    padding-top: 1rem;
    border-top: 1px solid #f3f4f6;
  }

  &__price-amount {
    font-size: 1.5rem;
    font-weight: 700;
    color: #1f1f1f;
  }

  &__price-period {
    font-size: 0.875rem;
    color: #737373;
  }
}

.billing-payment-failed {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
  margin-top: 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  color: #dc2626;

  svg {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
  }
}

// --------------------------------------------
// Plans Section
// --------------------------------------------

.billing-plans-section {
  margin-top: 2rem;
}

.billing-plans-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
}

.billing-plans-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1f1f1f;
}

// Toggle Switch
.billing-toggle {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0.75rem;
  background: #f3f4f6;
  border-radius: 9999px;

  &__label {
    font-size: 0.8125rem;
    font-weight: 500;
    color: #a3a3a3;
    user-select: none;
    transition: color 0.15s ease;
    display: flex;
    align-items: center;
    gap: 0.375rem;

    &--active {
      color: #1f1f1f;
    }
  }

  &__save {
    font-size: 0.625rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.025em;
    padding: 0.125rem 0.375rem;
    background: #dcfce7;
    color: #166534;
    border-radius: 9999px;
  }

  &__switch {
    position: relative;
    width: 2.5rem;
    height: 1.5rem;
    background: #d1d5db;
    border: none;
    border-radius: 9999px;
    cursor: pointer;
    transition: background 0.2s ease;

    &--active {
      background: var(--accent-color, #3b82f6);
    }
  }

  &__knob {
    position: absolute;
    top: 3px;
    left: 3px;
    width: 1.125rem;
    height: 1.125rem;
    background: #fff;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
    transition: transform 0.2s ease;

    .billing-toggle__switch--active & {
      transform: translateX(1rem);
    }
  }
}

// Plan Cards Grid
.billing-plans-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.25rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

// Plan Card
.billing-plan-card {
  position: relative;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.2s ease;

  &:hover:not(.billing-plan-card--disabled) {
    border-color: #d1d5db;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  &--current {
    border-color: var(--accent-color, #3b82f6);
    border-width: 2px;
  }

  &--disabled {
    opacity: 0.6;
    background: #f9fafb;
    pointer-events: none;
  }

  &__content {
    padding: 1.5rem;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 1rem;
  }

  &__name {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f1f1f;
  }

  &__current-badge {
    font-size: 0.625rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    padding: 0.25rem 0.5rem;
    background: #dbeafe;
    color: #1e40af;
    border-radius: 9999px;
  }

  &__pricing {
    display: flex;
    align-items: baseline;
    gap: 0.25rem;
    margin-bottom: 1.25rem;
  }

  &__price {
    font-size: 2rem;
    font-weight: 700;
    color: #1f1f1f;
    line-height: 1;
  }

  &__period {
    font-size: 0.875rem;
    color: #737373;
  }

  &__action {
    margin-bottom: 1.25rem;
  }

  &__features {
    list-style: none;
    padding: 0;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;

    li {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      font-size: 0.8125rem;
      color: #5c5c5c;

      svg {
        width: 1rem;
        height: 1rem;
        color: #10b981;
        flex-shrink: 0;
        margin-top: 1px;
      }
    }
  }
}

// Plan Buttons
.billing-plan-btn {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;

  &--current {
    background: #f3f4f6;
    color: #a3a3a3;
    cursor: not-allowed;
  }

  &--upgrade {
    background: var(--accent-color, #3b82f6);
    color: #fff;

    &:hover:not(:disabled) {
      filter: brightness(1.1);
      transform: translateY(-1px);
    }

    &:disabled {
      opacity: 0.5;
      cursor: not-allowed;
    }
  }

  &--downgrade {
    background: #fff;
    color: #404040;
    border: 1px solid #d1d5db;

    &:hover {
      background: #f9fafb;
      border-color: #9ca3af;
    }
  }
}

// Enterprise Card
.billing-enterprise {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  margin-top: 1.5rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #1f2937 0%, #374151 100%);
  border-radius: 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }

  &__content {
    display: flex;
    align-items: center;
    gap: 1rem;

    @media (max-width: 768px) {
      flex-direction: column;
    }
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    flex-shrink: 0;

    svg {
      width: 1.5rem;
      height: 1.5rem;
      color: #fff;
    }
  }

  &__text {
    h4 {
      font-size: 1rem;
      font-weight: 600;
      color: #fff;
      margin: 0 0 0.25rem 0;
    }

    p {
      font-size: 0.875rem;
      color: #a3a3a3;
      margin: 0;
    }
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 1.5rem;
    background: #fff;
    color: #1f1f1f;
    font-size: 0.875rem;
    font-weight: 600;
    border-radius: 10px;
    text-decoration: none;
    white-space: nowrap;
    transition: all 0.15s ease;

    &:hover {
      background: #f3f4f6;
      text-decoration: none;
      transform: translateY(-1px);
    }

    svg {
      width: 1rem;
      height: 1rem;
    }
  }
}

// --------------------------------------------
// Dialog Styles
// --------------------------------------------

.message-dialog {
  .dialog-heading {
    line-height: 1.4;
    color: #404040;
  }
}

// --------------------------------------------
// Animations
// --------------------------------------------

.fade-slide-enter-active,
.fade-slide-leave-active {
  transition: all 0.3s ease;
}

.fade-slide-enter-from,
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
